import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'

const ThemeContext = createContext(null)

const THEME_STORAGE_KEY = 'theme'
const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)'

function normalizeTheme(theme) {
  return theme === 'light' || theme === 'dark' ? theme : 'system'
}

function getStoredTheme() {
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY))
  } catch (_error) {
    return 'system'
  }
}

function getSystemTheme() {
  return window.matchMedia?.(SYSTEM_THEME_QUERY).matches ? 'dark' : 'light'
}

function applyResolvedTheme(theme, resolvedTheme) {
  const root = document.documentElement

  root.classList.toggle('dark', resolvedTheme === 'dark')
  root.dataset.theme = resolvedTheme
  root.dataset.themeChoice = theme
  root.style.colorScheme = resolvedTheme

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolvedTheme === 'dark' ? '#0D1015' : '#F6F8FA')
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme)
  const [systemTheme, setSystemTheme] = useState(getSystemTheme)
  const resolvedTheme = theme === 'system' ? systemTheme : theme

  useLayoutEffect(() => {
    applyResolvedTheme(theme, resolvedTheme)
  }, [resolvedTheme, theme])

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(SYSTEM_THEME_QUERY)
    if (!mediaQuery) return undefined

    const handleChange = (event) => {
      setSystemTheme(event.matches ? 'dark' : 'light')
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener?.(handleChange)
    return () => mediaQuery.removeListener?.(handleChange)
  }, [])

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY && event.key !== null) return
      setThemeState(normalizeTheme(event.newValue))
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const setTheme = useCallback((nextTheme) => {
    const normalizedTheme = normalizeTheme(nextTheme)

    try {
      if (normalizedTheme === 'system') {
        window.localStorage.removeItem(THEME_STORAGE_KEY)
      } else {
        window.localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme)
      }
    } catch (_error) {
      // Theme selection still applies for this session when storage is unavailable.
    }

    setThemeState(normalizedTheme)
  }, [])

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [resolvedTheme, setTheme, theme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}
